import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaComment, FaArrowUp, FaArrowDown, FaArrowRight } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  HeaderButton,
  BackButton,
  Title,
  Content,
  MainSection
} from '../common/styles';
import styled from '@emotion/styled';
import FeedbackReportContent from './FeedbackReportContent';

// Container for modal usage - fills parent height instead of 100vh
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
  height: 100%;
  overflow: hidden;
`;
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { COLORS } from '../../constants/radar';
import { Box, Typography } from '@mui/material';
import Modal from '../common/Modal';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackApi, skillsApi } from '../../services/api';
import { ASSIGNMENT_STATUSES } from '../../constants/assignmentStatuses';
import { AssignmentFrozenBadge } from '../common/AssignmentFrozenBadge';
import { REVIEW_TYPES } from '../../constants/reviewTypes';
import { CommentsField, DirtyStateContext } from '../EmployeesRepository/EmployeeForm/components/AssignmentDetails/components/ReviewerCompetencyList/CommentsField';
import { MIN_COMMENTS_LENGTH } from '../../constants/assignmentConstants';

const FeedbackActionsModal = ({ isOpen, onClose, assignmentId, onFeedbackSubmitted }) => {
  const [startComments, setStartComments] = useState('');
  const [stopComments, setStopComments] = useState('');
  const [continueComments, setContinueComments] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [savingFieldId, setSavingFieldId] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const { showAlert } = useAlert();

  // Use a ref to track the latest comments for auto-save without stale closures
  const commentsRef = React.useRef({ startComments: '', stopComments: '', continueComments: '' });

  // Fetch draft from backend every time the modal opens
  useEffect(() => {
    if (!isOpen || !assignmentId) {
      return;
    }

    const loadDraft = async () => {
      setLoadingDraft(true);
      try {
        const draft = await skillsApi.getAssignmentFeedbackActions(assignmentId);
        if (draft && !draft.completedAt) {
          setStartComments(draft.startComments || '');
          setStopComments(draft.stopComments || '');
          setContinueComments(draft.continueComments || '');
          commentsRef.current = {
            startComments: draft.startComments || '',
            stopComments: draft.stopComments || '',
            continueComments: draft.continueComments || ''
          };
        }
      } catch (error) {
        // Draft loading is best-effort — if it fails, the user starts with empty fields
        console.error('Failed to load draft feedback actions:', error);
      } finally {
        setLoadingDraft(false);
      }
    };

    loadDraft();
  }, [isOpen, assignmentId]);

  const handleSubmit = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      await feedbackApi.submitFinalFeedbackActions(assignmentId, {
        startComments,
        stopComments,
        continueComments
      });

      showAlert('Final feedback actions submitted successfully', 'success');
      resetForm();
      onClose();

      // Notify parent component that feedback was submitted successfully
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }
    } catch (error) {
      showAlert(error.message || 'Failed to submit feedback actions', 'error', error);
    }
  };

  const resetForm = () => {
    setStartComments('');
    setStopComments('');
    setContinueComments('');
    commentsRef.current = { startComments: '', stopComments: '', continueComments: '' };
    setShowConfirmation(false);
    setIsDirty(false);
    setSavingFieldId(null);
  };

  // Check if all feedback fields have content and meet minimum length
  const hasValidFeedback = startComments.trim().length >= MIN_COMMENTS_LENGTH &&
                          stopComments.trim().length >= MIN_COMMENTS_LENGTH &&
                          continueComments.trim().length >= MIN_COMMENTS_LENGTH;

  // Auto-save handler that sends all three fields (preserving existing values)
  const autoSaveDraft = useCallback(async (field, value) => {
    commentsRef.current[field] = value;
    await skillsApi.setAssignmentFeedbackActions(assignmentId, {
      startComments: commentsRef.current.startComments,
      stopComments: commentsRef.current.stopComments,
      continueComments: commentsRef.current.continueComments
    });
  }, [assignmentId]);

  const handleStartChange = async (value) => {
    setStartComments(value);
    return autoSaveDraft('startComments', value);
  };

  const handleStopChange = async (value) => {
    setStopComments(value);
    return autoSaveDraft('stopComments', value);
  };

  const handleContinueChange = async (value) => {
    setContinueComments(value);
    return autoSaveDraft('continueComments', value);
  };

  return (
    <DirtyStateContext.Provider value={{ isDirty, setIsDirty, savingFieldId, setSavingFieldId }}>
      <>
        <Modal
          isOpen={isOpen && !showConfirmation}
          onClose={() => {
            resetForm();
            onClose();
          }}
          title="Provide Final Feedback Actions"
          submitText="Submit Feedback Actions"
          cancelText="Close"
          onSubmit={handleSubmit}
          maxWidth="lg"
          submitDisabled={!hasValidFeedback || isDirty}
          cancelDisabled={isDirty}
          disableOverlayClose
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic', flexShrink: 0 }}>
            Your progress is saved automatically as you type. You can close this dialog and come back later to continue.
          </Typography>
          <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', minHeight: 0, flex: 1 }}>
            {/* START Actions */}
            <Box sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1.5,
              borderLeftWidth: 4,
              borderLeftColor: '#10B981'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <FaArrowUp size={14} color="#10B981" style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" sx={{ color: '#10B981', fontWeight: 600 }}>
                  START
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  — What should the individual start doing to improve their performance?
                </Typography>
              </Box>
              <CommentsField
                initialComments={startComments}
                onCommentChange={handleStartChange}
                disabled={false}
                skillId="start-actions"
                reviewerId="final-feedback"
                required={true}
                minContentsLength={MIN_COMMENTS_LENGTH}
                fieldId="final-feedback-start-actions"
                placeholder="Suggest new actions tied to development or growth opportunities."
                rows={4}
              />
            </Box>

            {/* STOP Actions */}
            <Box sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1.5,
              borderLeftWidth: 4,
              borderLeftColor: '#EF4444'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <FaArrowDown size={14} color="#EF4444" style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" sx={{ color: '#EF4444', fontWeight: 600 }}>
                  STOP
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  — What should the individual stop doing or reduce?
                </Typography>
              </Box>
              <CommentsField
                initialComments={stopComments}
                onCommentChange={handleStopChange}
                disabled={false}
                skillId="stop-actions"
                reviewerId="final-feedback"
                required={true}
                minContentsLength={MIN_COMMENTS_LENGTH}
                fieldId="final-feedback-stop-actions"
                placeholder="Focus on behavior change that affects team performance or individual growth."
                rows={4}
              />
            </Box>

            {/* CONTINUE Actions */}
            <Box sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1.5,
              borderLeftWidth: 4,
              borderLeftColor: COLORS.PRIMARY
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <FaArrowRight size={14} color={COLORS.PRIMARY} style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
                  CONTINUE
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  — What is the individual doing well and should continue?
                </Typography>
              </Box>
              <CommentsField
                initialComments={continueComments}
                onCommentChange={handleContinueChange}
                disabled={false}
                skillId="continue-actions"
                reviewerId="final-feedback"
                required={true}
                minContentsLength={MIN_COMMENTS_LENGTH}
                fieldId="final-feedback-continue-actions"
                placeholder="Focus on strengths worth reinforcing."
                rows={4}
              />
            </Box>
          </Box>
        </Modal>

        <Modal
          isOpen={isOpen && showConfirmation}
          onClose={() => setShowConfirmation(false)}
          title="Confirm Feedback Actions Submission"
          submitText="Confirm and Submit"
          cancelText="Go Back and Edit"
          onSubmit={handleConfirmSubmit}
          onCancel={() => setShowConfirmation(false)}
          maxWidth="lg"
          submitDisabled={!hasValidFeedback}
        >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to submit this final feedback actions? This action cannot be undone.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {startComments && (
              <Box sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                borderLeftWidth: 4,
                borderLeftColor: '#10B981'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FaArrowUp size={16} color="#10B981" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle1" sx={{ color: '#10B981', fontWeight: 500 }}>
                    START
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    pl: 2.5
                  }}
                >
                  {startComments}
                </Typography>
              </Box>
            )}
            
            {stopComments && (
              <Box sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                borderLeftWidth: 4,
                borderLeftColor: '#EF4444'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FaArrowDown size={16} color="#EF4444" style={{ marginRight: 8 }} />
                  <Typography variant="subtitle1" sx={{ color: '#EF4444', fontWeight: 500 }}>
                    STOP
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    pl: 2.5
                  }}
                >
                  {stopComments}
                </Typography>
              </Box>
            )}
            
            {continueComments && (
              <Box sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                borderLeftWidth: 4,
                borderLeftColor: COLORS.PRIMARY
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FaArrowRight size={16} color={COLORS.PRIMARY} style={{ marginRight: 8 }} />
                  <Typography variant="subtitle1" sx={{ color: COLORS.PRIMARY, fontWeight: 500 }}>
                    CONTINUE
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    pl: 2.5
                  }}
                >
                  {continueComments}
                </Typography>
              </Box>
            )}
            
            {!hasValidFeedback && (
              <Box sx={{ 
                border: '1px solid',
                borderColor: COLORS.DESTRUCTIVE + '40',
                borderRadius: 1,
                p: 2,
                backgroundColor: COLORS.DESTRUCTIVE + '10'
              }}>
                <Typography variant="body2" color={COLORS.DESTRUCTIVE}>
                  {`All three feedback sections (START, STOP, CONTINUE) must be filled with at least ${MIN_COMMENTS_LENGTH} characters each. Please go back and complete all sections.`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
      </>
    </DirtyStateContext.Provider>
  );
};

const EmployeeFeedbackReport = ({ assignmentId, inModal = false }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility();
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [hasFeedbackOnDemand, setHasFeedbackOnDemand] = useState(false);
  const [reportEmployeeId, setReportEmployeeId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState(null);
  // ADR-014 §7b: true when the parent feedback session was closed (snapshot
  // close). The report stays viewable, but no actions may be provided on it.
  const [isSessionLocked, setIsSessionLocked] = useState(false);
  const [reviewType, setReviewType] = useState(null);
  const [lastReviewerId, setLastReviewerId] = useState(null);

  const currentAssignmentId = assignmentId || params.assignmentId;

  // Check if opened in a modal (via prop or query parameter for backward compatibility)
  const searchParams = new URLSearchParams(location.search);
  const isInModal = inModal || searchParams.get('inModal') === 'true';

  // Extract scroll state for potential restoration (from My Feedback Results)
  const scrollState = location.state?.scrollState;
  const returnPath = location.state?.returnPath;

  const handleBack = useCallback(() => {
    // If we have scroll state from a list view, restore it
    if (returnPath && scrollState) {
      navigate(returnPath, {
        state: {
          fromDetail: true,
          scrollState,
          needsRestore: true,
          hideContentUntilScrolled: true,
          timestamp: Date.now()
        }
      });
    } else {
      // Fallback to browser back (existing behavior for other callers)
      navigate(-1);
    }
  }, [navigate, returnPath, scrollState]);

  // Determine back button text based on return context
  const getBackButtonText = () => {
    const returnContext = location.state?.returnContext;
    if (returnContext === 'individual-assignment') {
      return 'Back to Individual Assignment Report';
    } else if (returnContext === 'feedback-session') {
      return 'Back to Feedback Session Report';
    }
    // Default fallback
    return 'Back to Feedback Session Report';
  };

  const handleReportDataLoaded = useCallback((data) => {
    setHasFeedbackOnDemand(data.hasFeedbackOnDemand);
    setReportEmployeeId(data.reportEmployeeId);
    setAssignmentStatus(data.assignmentStatus);
    setIsSessionLocked(!!data.isLocked);

    // Extract only the specific data needed for permission checks
    const reportData = data.fullReportData;
    setReviewType(reportData?.reviewProcess?.[0]?.skillsMatrixSession?.reviewType || null);
    
    // Find the last reviewer ID if it's a sequential review
    if (reportData?.feedbackSession?.reviewType === REVIEW_TYPES.SEQUENTIAL && reportData.reviewers) {
      const sortedReviewers = [...reportData.reviewers].sort((a, b) => (b.order || 0) - (a.order || 0));
      const lastReviewer = sortedReviewers[0];
      setLastReviewerId(lastReviewer?.employee?.id || null);
    } else {
      setLastReviewerId(null);
    }
    
    setPermissionsLoaded(true);
  }, []); // No dependencies since it only calls state setters

  const handleFeedbackSubmitted = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
    setHasFeedbackOnDemand(true);
    setShowButton(false);
  }, []);

  useEffect(() => {
    if (!permissionsLoaded || !user) {
      setShowButton(false);
      return;
    }
    
    // Check if user has general ability to provide feedback actions
    const canProvideFeedbackActions = (reportEmployeeId) => {
      const feedbackReportFinalActionsObject = {
        employeeId: reportEmployeeId,
        __type: SUBJECTS.FEEDBACK_REPORT_FINAL_ACTIONS
      };
      
      return ability.can(ACTIONS.UPDATE, feedbackReportFinalActionsObject);
    };
   
    //TODO for the sequential review, the actions are not available from here anymore
    // Check if user is the last reviewer in a sequential review
    // const isLastReviewerInSequential = () => {
    //   return reviewType === REVIEW_TYPES.SEQUENTIAL && lastReviewerId === user?.id;
    // };
    
    const hasGeneralPermission = canProvideFeedbackActions(reportEmployeeId);
    //const isLastReviewer = isLastReviewerInSequential();
    const isParallelReview = reviewType === REVIEW_TYPES.PARALLEL;
    const isAssignmentReadyForFeedbackActions = assignmentStatus === ASSIGNMENT_STATUSES.PENDING_ACTIONS;

    // ADR-014 §7: a snapshot-closed session is frozen — the post-review
    // Start/Stop/Continue actions can no longer be provided (the API rejects
    // them with 409), so the affordance must not be offered.
    setShowButton((hasGeneralPermission /*|| isLastReviewer*/) && isParallelReview && !hasFeedbackOnDemand && isAssignmentReadyForFeedbackActions && !isSessionLocked);

  }, [permissionsLoaded, user, ability, hasFeedbackOnDemand, reportEmployeeId, assignmentStatus, reviewType, lastReviewerId, isSessionLocked]);

  const WrapperComponent = isInModal ? ModalContainer : Container;

  return (
    <WrapperComponent>
      {!isInModal && (
        <Header>
          <HeaderLeft>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> {getBackButtonText()}
            </BackButton>
            <Title>
              Individual Feedback Report
            </Title>
            {/* ADR-014 §10k: this assignment never finished before the session
                was closed, so it is frozen where it stopped. The session-level
                "Closed" chip on the session line (§10j) says the cycle ended;
                this one says what happened to THIS piece of work. `status` puts
                where it stopped into the tooltip. HeaderLeft is a flex row with
                gap, so no margin is needed. */}
            {isSessionLocked
              && assignmentStatus
              && assignmentStatus !== ASSIGNMENT_STATUSES.COMPLETED && (
              <AssignmentFrozenBadge status={assignmentStatus} />
            )}
          </HeaderLeft>
          {showButton && (
            <HeaderButton
              onClick={() => setModalOpen(true)}
            >
              <FaComment />
              Provide Feedback Actions
            </HeaderButton>
          )}
        </Header>
      )}
      <Content>
        <MainSection sx={{ maxWidth: "1200px", mx: "auto" }}>
          <FeedbackReportContent
            key={refreshTrigger}
            assignmentId={currentAssignmentId}
            onDataLoaded={handleReportDataLoaded}
          />
        </MainSection>
      </Content>

      <FeedbackActionsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        assignmentId={currentAssignmentId}
        onFeedbackSubmitted={handleFeedbackSubmitted}
      />
    </WrapperComponent>
  );
};

export default EmployeeFeedbackReport; 